"use client";

import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  MoreHorizontal,
  Upload,
  Archive,
  Copy,
  RotateCcw,
  Download,
  Trash2,
  Pencil,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  createVersion,
  updateVersion,
  publishVersion,
  archiveVersion,
  unarchiveVersion,
  deleteVersion,
  duplicateVersion,
  rollbackToVersion,
  downloadVersionExport,
} from "@/lib/api/versions";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useIsAdmin } from "@/lib/auth/store";
import { getErrorMessage } from "@/lib/api/client";
import { formatDate } from "@/lib/utils";
import type { SimulatorVersion } from "@/types";

interface VersionsTabProps {
  simulatorId: number;
  versions: SimulatorVersion[];
  selectedVersionId: number | null;
  onVersionSelect: (id: number) => void;
}

export function VersionsTab({
  simulatorId,
  versions,
  selectedVersionId,
  onVersionSelect,
}: VersionsTabProps) {
  const isAdmin = useIsAdmin();
  const queryClient = useQueryClient();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newVersionName, setNewVersionName] = useState("");
  const [newVersionNotes, setNewVersionNotes] = useState("");
  const [versionToDelete, setVersionToDelete] = useState<SimulatorVersion | null>(null);
  const [versionToRename, setVersionToRename] = useState<SimulatorVersion | null>(null);
  const [renameValue, setRenameValue] = useState("");

  const invalidateSimulator = () => {
    queryClient.invalidateQueries({ queryKey: ["simulator", simulatorId] });
  };

  const createMutation = useMutation({
    mutationFn: ({ name, notes }: { name?: string; notes?: string }) => 
      createVersion(simulatorId, { name: name || null, notes: notes || null }),
    onSuccess: (data) => {
      invalidateSimulator();
      toast.success("Version créée");
      onVersionSelect(data.id);
      setCreateDialogOpen(false);
      setNewVersionName("");
      setNewVersionNotes("");
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  const renameMutation = useMutation({
    mutationFn: ({ versionId, name }: { versionId: number; name: string | null }) =>
      updateVersion(versionId, { name }),
    onSuccess: () => {
      invalidateSimulator();
      toast.success("Nom de version mis à jour");
      setVersionToRename(null);
      setRenameValue("");
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  const publishMutation = useMutation({
    mutationFn: publishVersion,
    onSuccess: () => {
      invalidateSimulator();
      toast.success("Version publiée");
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  const archiveMutation = useMutation({
    mutationFn: archiveVersion,
    onSuccess: () => {
      invalidateSimulator();
      toast.success("Version archivée");
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  const unarchiveMutation = useMutation({
    mutationFn: unarchiveVersion,
    onSuccess: () => {
      invalidateSimulator();
      toast.success("Version désarchivée et republiée");
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  const duplicateMutation = useMutation({
    mutationFn: duplicateVersion,
    onSuccess: (data) => {
      invalidateSimulator();
      toast.success("Version dupliquée");
      onVersionSelect(data.id);
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  const rollbackMutation = useMutation({
    mutationFn: rollbackToVersion,
    onSuccess: (data) => {
      invalidateSimulator();
      toast.success("Rollback effectué");
      onVersionSelect(data.id);
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteVersion,
    onSuccess: () => {
      invalidateSimulator();
      toast.success("Version supprimée définitivement");
      setVersionToDelete(null);
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "draft":
        return <Badge variant="warning">Brouillon</Badge>;
      case "published":
        return <Badge variant="success">Publiée</Badge>;
      case "archived":
        return <Badge variant="secondary">Archivée</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Versions</CardTitle>
          <CardDescription>
            Gérez les différentes versions de ce simulateur
          </CardDescription>
        </div>
        {isAdmin && (
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Nouvelle version
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {versions.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <p>Aucune version disponible</p>
            {isAdmin && (
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => setCreateDialogOpen(true)}
              >
                Créer la première version
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {versions.map((version) => (
              <div
                key={version.id}
                onClick={() => onVersionSelect(version.id)}
                className={`flex items-center justify-between p-4 rounded-lg border cursor-pointer transition-colors ${
                  selectedVersionId === version.id
                    ? "border-primary bg-primary/5"
                    : "hover:bg-muted/50"
                }`}
              >
                <div className="flex items-center gap-4">
                  <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center font-bold text-lg">
                    {version.version_number}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">
                        Version {version.version_number}
                        {version.name && <span className="text-primary"> - {version.name}</span>}
                      </span>
                      {getStatusBadge(version.status)}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {version.notes || "Aucune note"}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Créée le {formatDate(version.created_at)}
                      {version.published_at && ` • Publiée le ${formatDate(version.published_at)}`}
                    </p>
                  </div>
                </div>

                {isAdmin && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {version.status === "draft" && (
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            publishMutation.mutate(version.id);
                          }}
                        >
                          <Upload className="mr-2 h-4 w-4" />
                          Publier
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          duplicateMutation.mutate(version.id);
                        }}
                      >
                        <Copy className="mr-2 h-4 w-4" />
                        Dupliquer
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            await downloadVersionExport(version.id);
                            toast.success("Export téléchargé");
                          } catch (error) {
                            toast.error("Erreur", { description: getErrorMessage(error) });
                          }
                        }}
                      >
                        <Download className="mr-2 h-4 w-4" />
                        Exporter JSON
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          setVersionToRename(version);
                          setRenameValue(version.name || "");
                        }}
                      >
                        <Pencil className="mr-2 h-4 w-4" />
                        Renommer
                      </DropdownMenuItem>
                      {version.status === "published" && (
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            rollbackMutation.mutate(version.id);
                          }}
                        >
                          <RotateCcw className="mr-2 h-4 w-4" />
                          Rollback
                        </DropdownMenuItem>
                      )}
                      {version.status !== "archived" && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              archiveMutation.mutate(version.id);
                            }}
                            className="text-destructive focus:text-destructive"
                          >
                            <Archive className="mr-2 h-4 w-4" />
                            Archiver
                          </DropdownMenuItem>
                        </>
                      )}
                      {version.status === "archived" && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              unarchiveMutation.mutate(version.id);
                            }}
                          >
                            <Upload className="mr-2 h-4 w-4" />
                            Désarchiver (republier)
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              setVersionToDelete(version);
                            }}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Supprimer définitivement
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Create Version Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouvelle version</DialogTitle>
            <DialogDescription>
              Créez une nouvelle version brouillon du simulateur
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nom (optionnel)</Label>
              <Input
                id="name"
                placeholder="Ex: Tarifs 2026, Version été..."
                value={newVersionName}
                onChange={(e) => setNewVersionName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">Notes (optionnel)</Label>
              <Textarea
                id="notes"
                placeholder="Notes de version..."
                value={newVersionNotes}
                onChange={(e) => setNewVersionNotes(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Annuler
            </Button>
            <Button
              onClick={() => createMutation.mutate({ name: newVersionName, notes: newVersionNotes })}
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? "Création..." : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename Version Dialog */}
      <Dialog open={!!versionToRename} onOpenChange={(open) => !open && setVersionToRename(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Renommer la version {versionToRename?.version_number}</DialogTitle>
            <DialogDescription>
              Donnez un nom personnalisé à cette version pour l&apos;identifier plus facilement.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="rename">Nom de la version</Label>
              <Input
                id="rename"
                placeholder="Ex: Tarifs 2026, Version été..."
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVersionToRename(null)}>
              Annuler
            </Button>
            <Button
              onClick={() => versionToRename && renameMutation.mutate({ 
                versionId: versionToRename.id, 
                name: renameValue || null 
              })}
              disabled={renameMutation.isPending}
            >
              {renameMutation.isPending ? "Enregistrement..." : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Version Confirmation Dialog */}
      <AlertDialog open={!!versionToDelete} onOpenChange={(open) => !open && setVersionToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer définitivement cette version ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. La version <strong>{versionToDelete?.version_number}</strong> et toutes ses données associées (variables, champs de formulaire, champs de sortie, tables de référence, etc.) seront définitivement supprimées.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => versionToDelete && deleteMutation.mutate(versionToDelete.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? "Suppression..." : "Supprimer définitivement"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

